<script setup lang="ts">
import type {
  EventsOverviewProduct,
  Product,
  ProductPreference,
} from "mws-ts-v2-sdk";

type PropsProduct =
  | Product
  | EventsOverviewProduct
  | (ProductPreference & { isFollowedByUser?: boolean });

type Props = {
  product: PropsProduct;
  hasFixedPrice: boolean;
};

const { product, hasFixedPrice } = defineProps<Props>();

const i18n = useI18n();

const isInStock = computed(() =>
  isEventOverviewProduct(product) ? true : product.inStock,
);

const labelTexts = computed(() => {
  const originalLabels = product.labels ?? [];

  if (!isInStock.value && hasFixedPrice) {
    return [translateWithFallback(i18n, "product.card.soldOut")];
  }

  return originalLabels
    .map((label) => {
      const labelText = typeof label === "string" ? label : label.text;

      if (!labelText) return "";

      // in case we don't have a translation for the label text, we fallback to the original text
      return translateWithFallback(
        i18n,
        `product.labels.${labelText}`,
        labelText,
      );
    })
    .filter(Boolean);
});
</script>

<template>
  <div
    class="absolute pointer-events-none overflow-auto flex flex-wrap gap-1 z-[1] top-2 left-2 w-[calc(100%-3.5rem)]"
  >
    <BaseTag
      v-for="label in labelTexts"
      :key="label"
    >
      {{ label }}
    </BaseTag>
  </div>
  <div
    v-if="product.highlights.length > 0"
    class="absolute pointer-events-none overflow-auto flex flex-wrap gap-1 z-[1] left-2 bottom-2 w-[calc(100%-1rem)]"
  >
    <BaseHighlight
      v-for="highlight in product.highlights"
      :key="highlight.id"
      :icon-url="highlight.logoUrl"
      :highlight="highlight.name"
    />
  </div>
</template>
